import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import CarDashboard from './CarDashboard';
import { Building2, Phone, Users, TrendingUp, LogIn, LogOut, Paintbrush } from 'lucide-react';
import apiClient from '../../services/api';
import { formatImgUrl } from '../../utils/imageUtils';
import './Dashboard.css';

const Dashboard = () => {
    const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const [entityData, setEntityData] = useState(JSON.parse(localStorage.getItem('entity') || 'null'));
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [entityRes, bookingsRes] = await Promise.all([
                    apiClient.get('/users/me/entity'),
                    apiClient.get('/bookings')
                ]);
                
                if (entityRes.data?.data) {
                    setEntityData(entityRes.data.data);
                    localStorage.setItem('entity', JSON.stringify(entityRes.data.data));
                }
                if (bookingsRes.data?.data) {
                    setBookings(bookingsRes.data.data);
                }
            } catch (error) {
                console.error("Failed to load dashboard data", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const coverPageUrl = entityData?.cover_page ? formatImgUrl(entityData.cover_page) : "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=2070";
    const displayName = entityData?.name || cachedUser.name || "Somstay Partner";
    const address = entityData?.address || "Partner Dashboard";
    const typeLabel = entityData?.type || "Business";
    const phoneLabel = entityData?.phone || "No Phone Registered";
    const roomsText = entityData?.total_rooms 
        ? `${entityData.available_rooms || 0} / ${entityData.total_rooms} Available` 
        : (entityData?.bedrooms ? `${entityData.bedrooms} Bedrooms` : "1 Unit Managed");

    // Compute KPIs
    const todayStr = new Date().toISOString().split('T')[0];
    const bookingsToday = bookings.filter(b => b.created_at && String(b.created_at).startsWith(todayStr)).length;
    const upcomingArrivals = bookings.filter(b => b.status === 'Confirmed' && new Date(b.check_in) >= new Date()).length;
    const activeOrders = bookings.filter(b => b.status === 'Pending' || b.status === 'Confirmed').length;

    // Last 7 days bookings count
    const last7DaysTotal = bookings.filter(b => {
        const bDate = new Date(b.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return bDate >= sevenDaysAgo;
    }).length;

    // Real Preparation Tasks
    const preparationTasks = bookings
        .filter(b => {
            const checkIn = new Date(b.check_in).toISOString().split('T')[0];
            const checkOut = new Date(b.check_out).toISOString().split('T')[0];
            return checkIn === todayStr || checkOut === todayStr;
        })
        .slice(0, 4)
        .map(b => {
            const isCheckIn = new Date(b.check_in).toISOString().split('T')[0] === todayStr;
            return {
                id: b.id,
                room: b.room_number || "TBD",
                type: isCheckIn ? "Check-in" : "Check-out",
                guest: b.guest_name || "N/A",
                time: isCheckIn ? "2:00 PM" : "11:00 AM",
                status: b.payment_status === 'Paid' ? "READY" : "PENDING",
                icon: isCheckIn ? <LogIn size={20} /> : <LogOut size={20} />,
                color: isCheckIn ? "green" : "blue"
            };
        });

    if (entityData?.entityType === 'car') {
        return (
            <div className="dashboard-container">
                <Sidebar />
                <CarDashboard entityData={entityData} bookings={bookings} />
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <Sidebar />

            <main className="main-content">
                {/* Hero Section */}
                <div className="hero-section">
                    <div className="hero-card">
                        <img src={coverPageUrl} alt="Cover" className="hero-bg" />
                        <div className="profile-overlay-outer">
                            <div className="hotel-profile-card">
                                <div className="hotel-logo-box">
                                    <HotelIcon />
                                </div>
                                <div className="hotel-details">
                                    <h1>{displayName}</h1>
                                    <p>{address}</p>
                                    <div className="status-badge-mini">
                                        <div className="dot"></div>
                                        {(entityData?.status || "ACTIVE").toUpperCase()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Badges */}
                <div className="info-grid">
                    <div className="info-item">
                        <div className="info-icon"><Building2 size={20} /></div>
                        <div>
                            <p className="info-label">BUSINESS TYPE</p>
                            <p className="info-value">{typeLabel}</p>
                        </div>
                    </div>
                    <div className="info-item">
                        <div className="info-icon"><Phone size={20} /></div>
                        <div>
                            <p className="info-label">CONTACT</p>
                            <p className="info-value">{phoneLabel}</p>
                        </div>
                    </div>
                    <div className="info-item">
                        <div className="info-icon"><Users size={20} /></div>
                        <div>
                            <p className="info-label">TOTAL UNITS / AVAILABILITY</p>
                            <p className="info-value">{roomsText}</p>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid">
                    <div className="stat-card border-blue">
                        <p className="stat-label">Bookings Today</p>
                        <p className="stat-value">{bookingsToday} <span className="stat-trend">Live</span></p>
                    </div>
                    <div className="stat-card border-blue">
                        <p className="stat-label">Upcoming Arrivals</p>
                        <p className="stat-value">{upcomingArrivals}</p>
                    </div>
                    <div className="stat-card border-blue">
                        <p className="stat-label">Active Orders</p>
                        <p className="stat-value">{activeOrders} <span className="stat-sub">Pending Review</span></p>
                    </div>
                    <div className="stat-card border-blue">
                        <p className="stat-label">Maintenance / Issues</p>
                        <p className="stat-value">0</p>
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="bottom-section">
                    <div className="preparation-section">
                        <div className="section-header">
                            <h2>Today's Preparation</h2>
                            <button className="view-all">View All</button>
                        </div>
                        <div className="task-list">
                            {preparationTasks.length > 0 ? (
                                preparationTasks.map(task => (
                                    <TaskItem 
                                        key={task.id}
                                        room={task.room} 
                                        type={task.type} 
                                        guest={task.guest} 
                                        time={task.time} 
                                        status={task.status} 
                                        color={task.color} 
                                        icon={task.icon} 
                                    />
                                ))
                            ) : (
                                <div className="no-tasks">No arrivals or departures scheduled for today.</div>
                            )}
                        </div>
                    </div>

                    <div className="trend-section">
                        <div className="section-header">
                            <h2>Orders Trend</h2>
                            <p className="section-sub">Last 7 days performance</p>
                        </div>
                        <div className="trend-content">
                            <div className="trend-placeholder">
                                <TrendingUp size={48} color="#e5e7eb" />
                                <div className="days-label">
                                    <span>MON</span><span>TUE</span><span>WED</span><span>THU</span><span>FRI</span><span className="active">SAT</span><span>SUN</span>
                                </div>
                            </div>
                            <div className="trend-footer">
                                <p>Weekly Total</p>
                                <h3>{last7DaysTotal} Bookings</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

// Helper Components
const HotelIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 21H21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 21V7L13 3V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 21V11L13 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 9V11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 15V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TaskItem = ({ room, type, guest, time, status, subStatus, color = 'green', icon }) => (
    <div className="task-item-container">
        <div className="task-item">
            <div className={`task-icon-container icon-${color}`}>
                {icon}
            </div>
            <div className="task-info">
                <p className="task-title">Room {room} - {type}</p>
                <p className="task-subtitle">{guest ? `Guest: ${guest} • ${time}` : `Status: ${subStatus}`}</p>
            </div>
            {status && <span className={`status-pill pill-${color}`}>{status}</span>}
        </div>
    </div>
);

export default Dashboard;
